// Centralized date/time formatting utilities
export const dateFormat = {
  // Format date as "Wed, Jan 15, 2024"
  formatDate(dateString) {
    if (!dateString) return 'No date'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date'
    
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  },

  // Format time as "2:30 PM"
  formatTime(dateString) {
    if (!dateString) return 'No time'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid time'
    
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  },

  // Format datetime as "Jan 15, 2024 at 2:30 PM"
  formatDateTime(dateString) {
    if (!dateString) return 'No date/time'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date/time'
    
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
    return `${dateStr} at ${timeStr}`
  },

  // Format relative time as "2 hours ago", "in 3 days", etc.
  formatRelativeTime(dateString) {
    if (!dateString) return 'No date'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date'
    
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (Math.abs(diffMinutes) < 1) return 'Just now'
    if (Math.abs(diffMinutes) < 60) {
      return diffMinutes > 0 ? `In ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'}` : `${Math.abs(diffMinutes)} minute${Math.abs(diffMinutes) === 1 ? '' : 's'} ago`
    }
    if (Math.abs(diffHours) < 24) {
      return diffHours > 0 ? `In ${diffHours} hour${diffHours === 1 ? '' : 's'}` : `${Math.abs(diffHours)} hour${Math.abs(diffHours) === 1 ? '' : 's'} ago`
    }
    if (Math.abs(diffDays) < 30) {
      return diffDays > 0 ? `In ${diffDays} day${diffDays === 1 ? '' : 's'}` : `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} ago`
    }

    // For longer periods, show formatted date
    return this.formatDate(dateString)
  },

  // Format time range as "2:30 PM - 4:00 PM"
  formatTimeRange(startDateTime, endDateTime) {
    const startTime = this.formatTime(startDateTime)
    const endTime = this.formatTime(endDateTime)
    return endDateTime ? `${startTime} - ${endTime}` : startTime
  },

  // Check if date is today
  isToday(dateString) {
    if (!dateString) return false
    const date = new Date(dateString)
    const today = new Date()
    return date.toDateString() === today.toDateString()
  },

  // Check if date is in the past
  isPast(dateString) {
    if (!dateString) return false
    const date = new Date(dateString)
    return date < new Date()
  },

  // Check if date is in the future
  isFuture(dateString) {
    if (!dateString) return false
    const date = new Date(dateString)
    return date > new Date()
  }
}